/* ===== Dues: treasury roster for one month ===== */

import { useEffect, useRef, useState } from 'react'
import type { DuesRosterStatusFilter, TenantContext } from '@zetyar/data-contracts'
import {
  addPeriods,
  formatPeriodLabel,
  periodFromDate,
  type DuesPeriodSummary,
  type DuesRosterEntry,
  type OrgDuesSettings,
} from '@zetyar/domain'
import { ChevronLeft, ChevronRight, MessageCircle, Search, Users } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { webDuesRepository } from '@/infrastructure/repositories/dues'
import { DueStatusBadge } from './DueStatusBadge'
import { RecordPaymentModal } from './RecordPaymentModal'
import { PAYMENT_METHOD_LABELS, duesErrorMessage, formatMoney } from './duesFormat'

interface DuesAdminViewProps {
  readonly context: TenantContext
  readonly settings: OrgDuesSettings
  readonly canManage: boolean
  readonly organizationName: string
}

type StatusFilter = 'all' | DuesRosterStatusFilter

const PAGE_SIZE = 25
const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'paid', label: 'Al día' },
  { value: 'exempt', label: 'Exentos' },
]

export function DuesAdminView({ context, settings, canManage, organizationName }: DuesAdminViewProps) {
  const [period, setPeriod] = useState(() => periodFromDate(new Date()))
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [summary, setSummary] = useState<DuesPeriodSummary | null>(null)
  const [entries, setEntries] = useState<DuesRosterEntry[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [recordTarget, setRecordTarget] = useState<DuesRosterEntry | null>(null)
  const [revertTarget, setRevertTarget] = useState<DuesRosterEntry | null>(null)
  const [revertReason, setRevertReason] = useState('')
  const [busyMembershipId, setBusyMembershipId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim())
      setOffset(0)
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchInput])

  useEffect(() => {
    const { listRoster, getPeriodSummary } = webDuesRepository
    if (!listRoster || !getPeriodSummary) return

    const requestId = ++requestIdRef.current
    setIsLoading(true)
    setError(null)
    Promise.all([
      getPeriodSummary(context, period),
      listRoster(context, {
        period,
        search: search || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: PAGE_SIZE,
        offset,
      }),
    ])
      .then(([nextSummary, page]) => {
        // A slower response for a month the user already left must not overwrite the current one.
        if (requestId !== requestIdRef.current) return
        setSummary(nextSummary)
        setEntries(page.entries)
        setTotal(page.total)
      })
      .catch((caught) => {
        if (requestId !== requestIdRef.current) return
        console.error('Error al cargar la nómina de cuotas:', caught)
        setError(duesErrorMessage(caught, 'No se pudo cargar la nómina de cuotas.'))
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsLoading(false)
      })
  }, [context, period, search, statusFilter, offset, reloadKey])

  const reload = () => setReloadKey((key) => key + 1)

  const changePeriod = (nextPeriod: string) => {
    setPeriod(nextPeriod)
    setOffset(0)
    setActionError(null)
  }

  const runAction = async (membershipId: string, action: () => Promise<void>) => {
    setBusyMembershipId(membershipId)
    setActionError(null)
    try {
      await action()
      reload()
    } catch (caught) {
      console.error('Error en la acción de cuotas:', caught)
      setActionError(duesErrorMessage(caught))
    } finally {
      setBusyMembershipId(null)
    }
  }

  const exempt = (entry: DuesRosterEntry, isExempt: boolean) => {
    const setExemption = webDuesRepository.setExemption
    if (!setExemption) return
    const question = isExempt
      ? `¿Eximir a ${entry.displayName} de la cuota de ${formatPeriodLabel(period)}?`
      : `¿Quitar la exención de ${entry.displayName} para ${formatPeriodLabel(period)}?`
    if (!confirm(question)) return
    void runAction(entry.membershipId, () =>
      setExemption(context, { membershipId: entry.membershipId, period, exempt: isExempt }),
    )
  }

  const confirmRevert = async () => {
    const target = revertTarget
    const revertPayment = webDuesRepository.revertPayment
    if (!target?.duePaymentId || !revertPayment) return
    const duePaymentId = target.duePaymentId
    await runAction(target.membershipId, () => revertPayment(context, duePaymentId, revertReason))
    setRevertTarget(null)
    setRevertReason('')
  }

  const reminderUrl = (entry: DuesRosterEntry) => {
    const lines = [
      `Recordatorio de cuota - ${organizationName}`,
      `Hola ${entry.displayName}, tu cuota de ${formatPeriodLabel(period)} por ${formatMoney(settings.monthlyAmount, settings.currency)} está pendiente.`,
      settings.accountNumber
        ? `Transferencia: ${[settings.bankName, settings.accountType, settings.accountNumber, settings.accountHolder, settings.accountRut].filter(Boolean).join(' / ')}`
        : null,
      settings.onlinePaymentEnabled ? 'También puedes pagar en línea desde Zetyar Nexo, en la sección Cuotas.' : null,
      'Si ya pagaste, envía tu comprobante a tesorería. Gracias.',
    ]
    return `https://wa.me/?text=${encodeURIComponent(lines.filter(Boolean).join('\n'))}`
  }

  const paidPercent =
    summary && summary.activeMembers > 0 ? Math.round((summary.paidCount / summary.activeMembers) * 100) : 0

  return (
    <Card>
      {/* CardHeader lays its children out in a row, so only the title and the month navigation go here. */}
      <CardHeader className="flex-wrap">
        <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-primary-600" />
            <div>
              <CardTitle className="text-base">Nómina de cuotas</CardTitle>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Estado de cada socio activo en el mes seleccionado.</p>
            </div>
          </div>
          <div className="flex items-center gap-1 rounded-[13px] border border-neutral-200 bg-neutral-50 p-1 dark:border-neutral-800 dark:bg-neutral-900">
            <button
              type="button"
              aria-label="Mes anterior"
              onClick={() => changePeriod(addPeriods(period, -1))}
              className="rounded-[10px] p-1.5 text-neutral-600 hover:bg-white dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-36 px-2 text-center text-sm font-semibold text-neutral-900 dark:text-neutral-100" aria-live="polite">
              {formatPeriodLabel(period)}
            </span>
            <button
              type="button"
              aria-label="Mes siguiente"
              onClick={() => changePeriod(addPeriods(period, 1))}
              className="rounded-[10px] p-1.5 text-neutral-600 hover:bg-white dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => changePeriod(periodFromDate(new Date()))}
              className="rounded-[10px] px-2.5 py-1 text-xs font-semibold text-primary-600 hover:bg-white dark:text-primary-300 dark:hover:bg-neutral-800"
            >
              Mes actual
            </button>
        </div>
      </CardHeader>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Metric label="Cuota mensual" value={formatMoney(settings.monthlyAmount, settings.currency)} detail={`Vence el día ${settings.dueDay}`} />
          <Metric
            label="Al día"
            value={summary ? String(summary.paidCount) : '...'}
            detail={summary ? `${paidPercent}% de ${summary.activeMembers} socios` : ''}
          />
          <Metric
            label="Pendientes"
            value={summary ? String(summary.pendingCount) : '...'}
            detail={summary?.processingCount ? `${summary.processingCount} con pago en proceso` : 'Por recaudar'}
          />
          <Metric label="Exentos" value={summary ? String(summary.exemptCount) : '...'} detail="Por decisión de la directiva" />
          <Metric
            label="Recaudado del mes"
            value={summary ? formatMoney(summary.collectedAmount, settings.currency) : '...'}
            detail="Registrado en Finanzas"
          />
        </div>

      <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="w-full md:w-72">
            <Input
              label="Buscar socio"
              placeholder="Nombre, N° de socio, cargo o correo"
              leftIcon={<Search className="h-4 w-4" />}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          <div role="group" aria-label="Filtrar por estado" className="flex flex-wrap gap-1.5">
            {FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                aria-pressed={statusFilter === filter.value}
                onClick={() => {
                  setStatusFilter(filter.value)
                  setOffset(0)
                }}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  statusFilter === filter.value
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-200'
                    : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
      </div>

      <CardContent className="-mx-5 mt-5 border-t border-neutral-100 pt-2 dark:border-neutral-800">
        {(error || actionError) && (
          <p role="alert" className="mx-5 mb-3 rounded-[13px] bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-300">
            {error ?? actionError}
          </p>
        )}

        {isLoading && entries.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">Cargando nómina...</p>
        ) : entries.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
            No hay socios que coincidan con los filtros.
          </p>
        ) : (
          <ul className={`divide-y divide-neutral-100 dark:divide-neutral-800 ${isLoading ? 'opacity-60' : ''}`}>
            {entries.map((entry) => {
              const isBusy = busyMembershipId === entry.membershipId
              return (
                <li key={entry.membershipId} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">{entry.displayName}</p>
                    <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                      {[
                        entry.memberNumber ? `Socio N° ${entry.memberNumber}` : null,
                        entry.position,
                        entry.institutionalEmail,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Sin datos adicionales'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <DueStatusBadge status={entry.dueStatus} />
                    {entry.dueStatus === 'paid' && entry.paymentMethod && (
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">{PAYMENT_METHOD_LABELS[entry.paymentMethod]}</span>
                    )}
                    {canManage && entry.dueStatus === null && (
                      <>
                        <Button size="sm" onClick={() => setRecordTarget(entry)} disabled={isBusy}>
                          Registrar pago
                        </Button>
                        <a
                          href={reminderUrl(entry)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border border-neutral-200 px-2.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
                        >
                          <MessageCircle className="h-3.5 w-3.5" /> Recordar
                        </a>
                        <Button size="sm" variant="ghost" onClick={() => exempt(entry, true)} isLoading={isBusy}>
                          Eximir
                        </Button>
                      </>
                    )}
                    {canManage && entry.dueStatus === 'paid' && entry.paymentMethod !== 'mercadopago' && (
                      <Button
                        size="sm"
                        variant="outline"
                        isLoading={isBusy}
                        onClick={() => {
                          setRevertReason('')
                          setRevertTarget(entry)
                        }}
                      >
                        Revertir
                      </Button>
                    )}
                    {canManage && entry.dueStatus === 'exempt' && (
                      <Button size="sm" variant="ghost" onClick={() => exempt(entry, false)} isLoading={isBusy}>
                        Quitar exención
                      </Button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 border-t border-neutral-100 px-5 py-4 text-xs text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
            <span>
              Mostrando {offset + 1}-{Math.min(offset + entries.length, total)} de {total}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={offset === 0 || isLoading} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                Anterior
              </Button>
              <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= total || isLoading} onClick={() => setOffset(offset + PAGE_SIZE)}>
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {recordTarget && (
        <RecordPaymentModal
          context={context}
          entry={recordTarget}
          initialPeriod={period}
          settings={settings}
          onClose={() => setRecordTarget(null)}
          onRecorded={() => {
            setRecordTarget(null)
            reload()
          }}
        />
      )}

      <Modal
        isOpen={Boolean(revertTarget)}
        onClose={() => setRevertTarget(null)}
        title="Revertir pago"
        footer={
          <>
            <Button variant="outline" onClick={() => setRevertTarget(null)}>
              Cancelar
            </Button>
            <Button variant="danger" isLoading={Boolean(revertTarget) && busyMembershipId === revertTarget?.membershipId} onClick={() => void confirmRevert()}>
              Revertir pago
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            Se anulará el pago completo de {revertTarget?.displayName}, incluidos todos los meses que cubre, y su ingreso se
            eliminará de Finanzas. La acción queda registrada en la auditoría.
          </p>
          <Input label="Motivo (opcional)" maxLength={500} value={revertReason} onChange={(event) => setRevertReason(event.target.value)} />
        </div>
      </Modal>
    </Card>
  )
}

function Metric({ label, value, detail }: { readonly label: string; readonly value: string; readonly detail: string }) {
  return (
    <div className="rounded-[13px] border border-neutral-200 bg-neutral-50/60 p-3 dark:border-neutral-800 dark:bg-neutral-900/60">
      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="mt-0.5 truncate text-lg font-bold text-neutral-900 dark:text-neutral-100">{value}</p>
      <p className="truncate text-[11px] text-neutral-500 dark:text-neutral-400">{detail}</p>
    </div>
  )
}
